import {Modal} from 'antd'
import {course_getCourseEnrollmentsByOwner_Api, courseEnrollment_getById_Api,} from 'apis'
import {LayoutContext} from 'components/layouts/Default/LayoutContext'
import _ from 'lodash'
import Async from 'modules/asyncCache/components/Async'
import useTranslate from 'modules/local/useTranslate'
import {useHistory} from 'modules/navigation/useRouter'
import React, {useCallback, useContext, useEffect, useMemo, useState,} from 'react'
import {useLocation} from 'react-use'
import {branch, compose} from 'recompose'
import {ContentNotFound} from 'views/AppLocals/ContentNotFound'
import {CourseActionTypes, enrollment_status_options,} from 'views/AppLocals/Course'
import {useCourse} from 'views/AppLocals/Course/useCourse'
import {showDeleteConfirmDialog} from 'views/AppLocals/factory/createConfirmDialog'
import createContextMenu from 'views/AppLocals/factory/createContextMenu'
import {bindQueryParam, bindQueryParams,} from 'views/AppLocals/functions/routerHelper'
import usePaginationHelper, {createPagerKey,} from 'views/AppLocals/hooks/usePaginationHelper'
import {getResponseItem, NullAsDefault} from 'views/Shared'
import {CourseApproveModal} from '../components/CourseApproveModal'
import CourseEnrollmentFilter from '../components/filters/CourseEnrollmentFilter'
import {renderCourseEnrollment} from '../functions/renderCourseEnrollment'
import {IoWarning} from "react-icons/io5";
import {BiTrash} from "react-icons/bi";
import {PiCheckCircle, PiXCircle} from "react-icons/pi";
import {copyLocationParam, getRouteSearchParams, useRouteSearchParam} from "../../../../envs/_current/history";
import LoadingBlock from "../../../Loading/LoadingBlock";

const CourseEnrollments = ({
                               owner_id,
                               owner_type,
                               filter_actions = {
                                   status: 'status',
                                   sortBy: 'sort-by',
                                   keyword: 'keyword',
                                   course_id: 'course-id',
                               },
                           }) => {
    const t = useTranslate()

    const history = useHistory()

    const location = useLocation()

    const {isSm} = useContext(LayoutContext)

    const sortBy = useRouteSearchParam(filter_actions.sortBy)

    const status = useRouteSearchParam(filter_actions.status)

    const course_id = useRouteSearchParam(filter_actions.course_id)

    const [action, setAction] = useState({
        type: undefined,
        value: undefined,
    })

    const onCancel = () => {
        setAction({})
    }

    let timeout
    useEffect(() => {
        const searchParams = getRouteSearchParams()
        searchParams.set(
            filter_actions.status,
            enrollment_status_options.pending.value
        )
        timeout = setTimeout(() => {
            history.replace({
                ...copyLocationParam(window.location ?? {}),
                search: '?' + searchParams.toString(),
            })
        })
        return () => clearTimeout(timeout)
    }, [])

    const {
        refreshKey,
        onReject = NullAsDefault,
        onApprove = NullAsDefault,
        onDeleteEnrollment = NullAsDefault,
    } = useCourse({
        onRejected: onCancel,
        onApproved: onCancel,
        onEnrollmentDeleted: onCancel,
    })

    const confirmReject = (id) => {
        if (id) {
            showDeleteConfirmDialog({
                icon: <IoWarning className="anticon"/>,
                translate: t,
                title: 'reject student',
                message: 'are you sure you want to reject this student',
                onOk: () => {
                    onReject({}, {id})
                },
            })
        }
    }

    const confirmDelete = (id) => {
        if (id) {
            showDeleteConfirmDialog({
                translate: t,
                onOk: () => {
                    onDeleteEnrollment({}, {id})
                },
            })
        }
    }

    const handleAction = (action, params) => {
        const course_id = params?.id
        switch (action) {
            case CourseActionTypes.ENROLLMENT_APPROVE:
                setAction({type: action, value: params})
                break
            case CourseActionTypes.ENROLLMENT_REJECT:
                confirmReject(course_id)
                break
            case CourseActionTypes.ENROLLMENT_DELETE:
                confirmDelete(course_id)
                break
            default:
                break
        }
    }

    const renderContextMenu = useCallback((item) => {
        let included_actions = []
        switch (item?.status) {
            case enrollment_status_options.pending.value:
                included_actions = [
                    CourseActionTypes.ENROLLMENT_REJECT,
                    CourseActionTypes.ENROLLMENT_APPROVE,
                    CourseActionTypes.ENROLLMENT_DELETE,
                ]
                break
            case enrollment_status_options.rejected.value:
                included_actions = [
                    CourseActionTypes.ENROLLMENT_APPROVE,
                    CourseActionTypes.ENROLLMENT_DELETE,
                ]
                break
            default:
                break
        }
        return createContextMenu(
            [
                {
                    label: 'approve',
                    icon: <PiCheckCircle size={14} className="text-green-500"/>,
                    key: CourseActionTypes.ENROLLMENT_APPROVE,
                },
                {
                    label: 'reject',
                    icon: <PiXCircle size={14} className="text-orange-500"/>,
                    key: CourseActionTypes.ENROLLMENT_REJECT,
                },
                {
                    label: 'delete',
                    icon: <BiTrash size={14} className="text-primary-300"/>,
                    key: CourseActionTypes.ENROLLMENT_DELETE,
                },
            ].filter(({key}) => included_actions.includes(key)),
            (key) => {
                handleAction(key, item)
            }
        )
    }, [])

    const modal = useMemo(() => {
        const {id} = action?.value ?? {}
        let title
        let Content = NullAsDefault
        let Wrapper = ({children}) => children({initialValues: {}})
        let params = {title: undefined}
        let modal_params = {}
        switch (action?.type) {
            case CourseActionTypes.ENROLLMENT_APPROVE:
                title = (
                    <div className="text-center font-bold text-color-000 uppercase tracking-wide">
                        {t('registration review form')}
                    </div>
                )
                params = {
                    owner_id,
                    owner_type,
                    onCancel,
                    onSubmit: (values) => {
                        onApprove(values, {id})
                    },
                }
                modal_params = {
                    width: 720,
                }
                Content = CourseApproveModal
                Wrapper = ({children}) => (
                    <Async
                        {...{
                            query: bindQueryParam({id}),
                            apiInfo: courseEnrollment_getById_Api,
                        }}>
                        {({isLoading, response}) =>
                            _.isFunction(children)
                                ? children({
                                    isLoading,
                                    initialValues: getResponseItem(response),
                                })
                                : children
                        }
                    </Async>
                )
                break
            default:
                break
        }
        return (
            <Modal
                className="custom-modal"
                footer={null}
                title={title}
                onCancel={onCancel}
                open={action?.type}
                destroyOnHidden={true}
                {...modal_params}>
                <Wrapper>
                    {({isLoading, initialValues}) => {
                        if (!!isLoading) {
                            return <LoadingBlock/>
                        }
                        return (
                            <Content
                                {...params}
                                {...{owner_id, owner_type}}
                                initialValues={initialValues}
                            />
                        )
                    }}
                </Wrapper>
            </Modal>
        )
    }, [action, owner_id, owner_type])

    const refresh_deps = useMemo(
        () => [
            owner_id,
            owner_type,
            refreshKey,
            `refreshToken=${_.get(location.state, 'refreshToken')}`,
        ],
        [owner_id, owner_type, refreshKey, location.state]
    )

    const [pager_widget] = usePaginationHelper({
        filter_params: {
            owner_id,
            owner_type,
        },
        onInit: ({onSortChange = NullAsDefault, onFilterChange = NullAsDefault}) => {
            onFilterChange({
                status,
                course_id,
            })
            onSortChange(sortBy)
        },
        pagerInfo: {
            RenderEntity: React.Fragment,
            PaginationMeta: NullAsDefault,
            apiInfo: course_getCourseEnrollmentsByOwner_Api,
            query: bindQueryParams([{id: owner_id}, {type: owner_type}]),
            renderItem: renderCourseEnrollment({
                renderContextMenu,
                statusFilter: status,
                owner_id,
            }),
            renderWrap: (child) => (
                <div className="mt-2 flex flex-col divide-y divide-gray-200 list-item-workspace">
                    {child}
                </div>
            )
        },
        SortByWidget: NullAsDefault,
        renderSortBy: NullAsDefault,
        isToggleDefault: true,
        query_params: filter_actions,
        dependencies: [owner_id, owner_type],
        withKey: createPagerKey(refresh_deps),
        AdvancedFilter: CourseEnrollmentFilter,
    })
    return (
        <div className="flex flex-col gap-3 222-222">
            <div className="flex flex-col flex-1">
                <h2 className="font-bold text-xl text-gray-800 tracking-wide">
                    {t('enrollments')}
                </h2>
                <div className="my-1 w-20 h-1 rounded-full bg-gray-400"/>
                <div className="mt-1 text-sm text-color-300 tracking-wider">
                    {t('registers for course description')}
                </div>
            </div>
            {pager_widget}
            {modal}
            <div className="h-16"/>
        </div>
    )
}

export default compose(
    branch(
        ({owner_id, owner_type}) => _.some([owner_id, owner_type], _.isEmpty),
        () => () => <ContentNotFound/>
    )
)(CourseEnrollments)
